import { UNREAD_COUNT_BROADCAST_CHANNEL } from "constants/streamConnection";
import { useEffect } from "react";
import { setUnreadMessages } from "store/slices/chatUnreadCount";
import { StreamChat } from "stream-chat";
import { useAppDispatch } from "./useAppDispatch";
import { useBroadcastChannel } from "./useBroadcastChannel";

type UnreadCountMessage = {
  userId: string;
  unreadCount: number;
};

const isUnreadCountMessage = (data: unknown): data is UnreadCountMessage =>
  typeof data === "object" &&
  data !== null &&
  "userId" in data &&
  typeof data.userId === "string" &&
  "unreadCount" in data &&
  typeof data.unreadCount === "number";

// Tabs with a live websocket broadcast unread count changes, and tabs whose
// websocket was closed while hidden pick them up, with no extra Stream calls.
export const useShareUnreadCountAcrossTabs = (
  chatClient: StreamChat | null
): void => {
  const dispatch = useAppDispatch();

  const handleMessage = ({ data }: MessageEvent<unknown>) => {
    if (!chatClient || chatClient.wsConnection?.isHealthy) return;
    if (!isUnreadCountMessage(data) || data.userId !== chatClient.userID) {
      return;
    }

    dispatch(setUnreadMessages(data.unreadCount));
  };

  const { postMessage } = useBroadcastChannel<UnreadCountMessage>(
    UNREAD_COUNT_BROADCAST_CHANNEL,
    handleMessage
  );

  useEffect(() => {
    if (!chatClient) return;

    const { unsubscribe } = chatClient.on((event) => {
      if (event.total_unread_count === undefined || !chatClient.userID) return;

      postMessage({
        userId: chatClient.userID,
        unreadCount: event.total_unread_count,
      });
    });

    return unsubscribe;
  }, [chatClient, postMessage]);
};
